import {
	USER_LIMITS,
	USER_MAIN_ROLES,
	type UserMainRole,
} from "@zplanem/responses"
import { type Locale, SUPPORTED_LOCALES } from "@zplanem/translations"
import type React from "react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../lib/api-client"
import { useAuth } from "../auth-context"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"

function fillTemplate(
	template: string,
	vars: Record<string, string | number>,
): string {
	return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""))
}

/**
 * Best-effort split of Firebase's single `displayName` ("John Doe",
 * "María del Carmen Ruiz") into our two-field shape. Google sign-in
 * populates displayName; email/password sign-up leaves it null, in
 * which case both halves come back empty.
 */
function splitDisplayName(displayName: string | null | undefined): {
	firstName: string
	lastName: string
} {
	const trimmed = displayName?.trim() ?? ""
	if (trimmed === "") return { firstName: "", lastName: "" }
	const [first, ...rest] = trimmed.split(/\s+/)
	return { firstName: first ?? "", lastName: rest.join(" ") }
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

export function OnboardingPage() {
	const { user: firebaseUser } = useAuth()
	const { profile, patchProfile } = useUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)

	const initialNames = splitDisplayName(firebaseUser?.displayName)
	const [firstName, setFirstName] = useState<string>(initialNames.firstName)
	const [lastName, setLastName] = useState<string>(initialNames.lastName)
	const [language, setLanguage] = useState<Locale>(
		(profile?.language as Locale | null) ?? locale,
	)
	const [mainRole, setMainRole] = useState<UserMainRole | "">("")
	const [acceptTerms, setAcceptTerms] = useState<boolean>(false)
	const [errors, setErrors] = useState<{
		firstName?: string
		lastName?: string
		mainRole?: string
		terms?: string
		form?: string
	}>({})
	const [submitting, setSubmitting] = useState(false)

	const recentlyCreated = profile?.createdAt
		? Date.now() - new Date(profile.createdAt).getTime() < TWENTY_FOUR_HOURS_MS
		: true
	const heading = recentlyCreated
		? t.onboarding.welcomeHeading
		: t.onboarding.justOneMoreThing

	// Splice the {link} placeholder in t.onboarding.termsLabel into JSX so the
	// link target is real markup, not a string.
	const [termsBefore, termsAfter] = t.onboarding.termsLabel.split("{link}")

	function validate(): boolean {
		const next: typeof errors = {}
		const trimmedFirst = firstName.trim()
		if (trimmedFirst.length === 0) {
			next.firstName = t.onboarding.errors.firstNameRequired
		} else if (trimmedFirst.length > USER_LIMITS.firstNameMax) {
			next.firstName = fillTemplate(t.onboarding.errors.firstNameTooLong, {
				max: USER_LIMITS.firstNameMax,
			})
		}
		const trimmedLast = lastName.trim()
		if (trimmedLast.length === 0) {
			next.lastName = t.onboarding.errors.lastNameRequired
		} else if (trimmedLast.length > USER_LIMITS.lastNameMax) {
			next.lastName = fillTemplate(t.onboarding.errors.lastNameTooLong, {
				max: USER_LIMITS.lastNameMax,
			})
		}
		if (mainRole === "") {
			next.mainRole = t.onboarding.errors.mainRoleRequired
		}
		if (!acceptTerms) {
			next.terms = t.onboarding.errors.termsRequired
		}
		setErrors(next)
		return Object.keys(next).length === 0
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!validate()) return
		setSubmitting(true)
		setErrors((prev) => ({ ...prev, form: undefined }))
		try {
			await patchProfile({
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				mainRole: mainRole as UserMainRole,
				language,
				acceptTerms: true,
			})
			// On success, isOnboardingComplete flips to true in UserContext;
			// the routing gate in app.tsx re-renders and the user lands on
			// /dashboard automatically.
		} catch (e) {
			const message =
				e instanceof ApiError ? e.message : t.onboarding.errors.unknown
			setErrors((prev) => ({ ...prev, form: message }))
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<div className="min-h-screen w-full bg-background flex items-center justify-center px-6 py-10">
			<div className="w-full max-w-md bg-muted/40 border border-border rounded-lg p-8">
				<h1 className="text-3xl font-black text-foreground tracking-tight mb-2">
					{heading}
				</h1>
				<p className="text-sm text-muted-foreground mb-8">
					{t.onboarding.subtitle}
				</p>

				<form onSubmit={onSubmit} className="space-y-5" noValidate>
					<div>
						<label
							htmlFor="onb-firstName"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.onboarding.firstNameLabel}
						</label>
						<input
							id="onb-firstName"
							type="text"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							placeholder={t.onboarding.firstNamePlaceholder}
							maxLength={USER_LIMITS.firstNameMax + 10}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							aria-invalid={Boolean(errors.firstName)}
							aria-describedby={
								errors.firstName ? "onb-firstName-error" : undefined
							}
						/>
						{errors.firstName ? (
							<p
								id="onb-firstName-error"
								className="text-sm text-destructive mt-1"
							>
								{errors.firstName}
							</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor="onb-lastName"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.onboarding.lastNameLabel}
						</label>
						<input
							id="onb-lastName"
							type="text"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							placeholder={t.onboarding.lastNamePlaceholder}
							maxLength={USER_LIMITS.lastNameMax + 10}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							aria-invalid={Boolean(errors.lastName)}
							aria-describedby={
								errors.lastName ? "onb-lastName-error" : undefined
							}
						/>
						{errors.lastName ? (
							<p
								id="onb-lastName-error"
								className="text-sm text-destructive mt-1"
							>
								{errors.lastName}
							</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor="onb-mainRole"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.onboarding.mainRoleLabel}
						</label>
						<select
							id="onb-mainRole"
							value={mainRole}
							onChange={(e) => setMainRole(e.target.value as UserMainRole | "")}
							className="form-select w-full rounded-xl bg-muted dark:bg-muted/60 border border-border pl-4 pr-9 py-3 text-foreground focus:border-ring outline-none"
							aria-invalid={Boolean(errors.mainRole)}
							aria-describedby={
								errors.mainRole ? "onb-mainRole-error" : undefined
							}
						>
							<option value="" disabled>
								{t.onboarding.mainRolePlaceholder}
							</option>
							{USER_MAIN_ROLES.map((role) => (
								<option key={role} value={role}>
									{t.userMainRoles[role]}
								</option>
							))}
						</select>
						{errors.mainRole ? (
							<p
								id="onb-mainRole-error"
								className="text-sm text-destructive mt-1"
							>
								{errors.mainRole}
							</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor="onb-language"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.onboarding.languageLabel}
						</label>
						<select
							id="onb-language"
							value={language}
							onChange={(e) => setLanguage(e.target.value as Locale)}
							className="form-select w-full rounded-xl bg-muted dark:bg-muted/60 border border-border pl-4 pr-9 py-3 text-foreground focus:border-ring outline-none"
						>
							{SUPPORTED_LOCALES.map((loc) => (
								<option key={loc} value={loc}>
									{t.language[loc]}
								</option>
							))}
						</select>
					</div>

					<div>
						<label className="flex items-start gap-2 text-sm text-foreground/80 cursor-pointer">
							<input
								type="checkbox"
								checked={acceptTerms}
								onChange={(e) => setAcceptTerms(e.target.checked)}
								className="mt-0.5 accent-ring"
								aria-invalid={Boolean(errors.terms)}
							/>
							<span>
								{termsBefore}
								{/* biome-ignore lint/a11y/useValidAnchor: T&S URL is intentional placeholder until a follow-up provides the real link target. */}
								<a href="#" className="underline">
									{t.onboarding.termsLink}
								</a>
								{termsAfter}
							</span>
						</label>
						{errors.terms ? (
							<p className="text-sm text-destructive mt-1">{errors.terms}</p>
						) : null}
					</div>

					{errors.form ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
						>
							{errors.form}
						</div>
					) : null}

					<Button
						type="submit"
						variant="brand"
						size="lg"
						disabled={submitting}
						className="w-full"
					>
						{t.onboarding.submit}
					</Button>
				</form>
			</div>
		</div>
	)
}
